import { requestAssetApi } from './getAssetById';
import {
  isGenericOfficeSearchPhrase,
  matchBrowseAssetTypeFromSpokenPhrase,
} from '../constants/filterableAssetTypes';
import { ISRAEL_COUNTRY_ID } from '../utils/assetCountryCurrency';
import { facilityAddonIconId } from '../utils/assetFacilityIcons';
import type { AppLanguage } from '../i18n/types';
import type { AppView } from '../navigation/AppNavigationContext';
import type { AssistantSearchIntent } from '../assistant/parseAssistantIntent';
import type { AssistantPriceConstraint } from '../assistant/assistantPriceConstraint';
import {
  assistantPriceToWire,
  emptyAssistantPriceWire,
  parseAssistantPriceFromBrowse,
  type AssistantPriceWireFields,
} from '../assistant/assistantPriceConstraint';
import {
  assistantScheduleToWire,
  emptyAssistantSchedule,
  mergeAssistantScheduleOverlay,
  nextAssistantScheduleDateKey,
  parseAssistantScheduleFromUtterance,
  parseAssistantScheduleFromWire,
  type AssistantSchedule,
} from '../assistant/assistantSchedule';

export const ASSISTANT_TURN_PATH = '/assistant/turn';

export type AssistantTurnAction = 'search' | 'refine' | 'faq' | 'propose_book' | 'clarify';

export type AssistantTurnLastSearchWire = {
  city: string | null;
  street: string | null;
  type: string | null;
  officeFamily: boolean | string | null;
  open24h: boolean;
  start_date: string | null;
  end_date: string | null;
  schedule?: ReturnType<typeof assistantScheduleToWire>;
} & AssistantPriceWireFields;

export type AssistantTurnBrowse = {
  city: string | null;
  street: string | null;
  officeFamily: boolean | string | null;
  type: string | null;
  open24h: boolean;
  /** Facility labels from the search assistant DTO (`Kitchen`, `Parking`, `WiFi`, …). */
  facilities: string[] | null;
  /** Raw leftover text from the turn — prefer `facilities` over this for browse. */
  listSearch: string | null;
  start_date: string | null;
  end_date: string | null;
  /** Slot start at browse root (also mirrored on `schedule` when present). */
  start_time: string | null;
  duration_minutes: number | null;
  /** Month bucket e.g. `2026-11` when the user said “בנובמבר”. */
  when_month: string | null;
  price: AssistantPriceConstraint | null;
  schedule: AssistantSchedule | null;
} & AssistantPriceWireFields;

export type AssistantTurnResponse = {
  say: string;
  action: AssistantTurnAction;
  browse: AssistantTurnBrowse;
  assetIds: string[];
  meta: Record<string, unknown> | null;
};

export type AssistantTurnBrowseParams = {
  catalogBrowse: true;
  returnView: 'home';
  listSearch: string | null;
  countryId: number | null;
  city: string | null;
  street: string | null;
  assetTypeLabel: string | null;
  officeFamily: boolean;
  open24h: boolean;
  whenDate: string | null;
  whenTime: string | null;
  assetIds: string[] | null;
  price: AssistantPriceConstraint | null;
  schedule: AssistantSchedule | null;
  amenityIcons?: string[] | null;
};

const TURN_ACTIONS = new Set<AssistantTurnAction>([
  'search',
  'refine',
  'faq',
  'propose_book',
  'clarify',
]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function trimString(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

function parseAssetIds(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }
  const seen = new Set<string>();
  const ids: string[] = [];
  for (const item of value) {
    const id = trimString(item);
    if (!id) {
      continue;
    }
    const key = id.toLowerCase();
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    ids.push(id);
  }
  return ids;
}

function parseOpen24h(value: unknown): boolean {
  if (value === true || value === 1 || value === '1' || value === 'true') {
    return true;
  }
  return false;
}

function parseStringList(value: unknown): string[] | null {
  if (!Array.isArray(value)) {
    return null;
  }
  const out: string[] = [];
  const seen = new Set<string>();
  for (const item of value) {
    const text = trimString(item);
    if (!text) {
      continue;
    }
    const key = text.toLowerCase();
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    out.push(text);
  }
  return out.length > 0 ? out : null;
}

function parseDurationMinutesField(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return Math.floor(value);
  }
  if (typeof value === 'string' && value.trim()) {
    const n = Number(value.trim());
    if (Number.isFinite(n) && n > 0) {
      return Math.floor(n);
    }
  }
  return null;
}

function parseHmClock(value: unknown): string | null {
  const raw = trimString(value);
  if (!raw) {
    return null;
  }
  const m = raw.match(/^(\d{1,2}):(\d{2})$/);
  if (!m) {
    return null;
  }
  const hour = Number(m[1]);
  const minute = Number(m[2]);
  if (hour < 0 || hour > 23 || minute < 0 || minute > 59) {
    return null;
  }
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

function parseWhenMonth(value: unknown): string | null {
  const raw = trimString(value);
  if (!raw) {
    return null;
  }
  return /^\d{4}-\d{2}$/.test(raw) ? raw : null;
}

/**
 * Map turn `browse.facilities` labels onto facility icon ids used by the local
 * amenity hard-filter (any catalog facility / transport / accessibility).
 */
export function amenityIconsFromTurnFacilities(
  facilities: string[] | null | undefined,
): string[] {
  if (!facilities?.length) {
    return [];
  }
  const out: string[] = [];
  const seen = new Set<string>();
  for (const raw of facilities) {
    const label = raw.trim();
    if (!label) {
      continue;
    }
    const icon = facilityAddonIconId(label);
    if (!icon || icon === 'default' || seen.has(icon)) {
      continue;
    }
    seen.add(icon);
    out.push(icon);
  }
  return out;
}

function mergeBrowseSlotIntoSchedule(
  schedule: AssistantSchedule | null,
  startTime: string | null,
  durationMinutes: number | null,
): AssistantSchedule | null {
  if (!startTime && durationMinutes == null && !schedule) {
    return null;
  }
  const base = schedule ?? emptyAssistantSchedule();
  const merged: AssistantSchedule = {
    frequency: base.frequency,
    weekdays: base.weekdays,
    closed_weekdays: base.closed_weekdays ?? [],
    start_time: startTime ?? base.start_time,
    duration_minutes: durationMinutes ?? base.duration_minutes,
    until_date: base.until_date,
  };
  if (
    !merged.frequency &&
    merged.weekdays.length === 0 &&
    (merged.closed_weekdays?.length ?? 0) === 0 &&
    !merged.start_time &&
    merged.duration_minutes == null &&
    !merged.until_date
  ) {
    return null;
  }
  return merged;
}

function parseBrowse(value: unknown): AssistantTurnBrowse {
  const emptyPrice = emptyAssistantPriceWire();
  if (!isRecord(value)) {
    return {
      city: null,
      street: null,
      officeFamily: null,
      type: null,
      open24h: false,
      facilities: null,
      listSearch: null,
      start_date: null,
      end_date: null,
      start_time: null,
      duration_minutes: null,
      when_month: null,
      price: null,
      schedule: null,
      ...emptyPrice,
    };
  }
  const schedule = parseAssistantScheduleFromWire(value.schedule);
  const startTime =
    parseHmClock(value.start_time) ?? parseHmClock(value.startTime);
  const durationMinutes =
    parseDurationMinutesField(value.duration_minutes) ??
    parseDurationMinutesField(value.durationMinutes);
  const price = parseAssistantPriceFromBrowse(value);
  // Years as until_date must not become a max price when schedule is present.
  const safePrice =
    schedule?.until_date && price && price.amount >= 1900 && price.amount <= 2100
      ? null
      : price;
  return {
    city: trimString(value.city),
    street: trimString(value.street),
    officeFamily:
      value.officeFamily === true || value.officeFamily === false
        ? value.officeFamily
        : trimString(value.officeFamily),
    type: trimString(value.type),
    open24h: parseOpen24h(value.open24h),
    facilities: parseStringList(value.facilities),
    listSearch: trimString(value.listSearch) ?? trimString(value.list_search),
    start_date: trimString(value.start_date) ?? trimString(value.startDate),
    end_date: trimString(value.end_date) ?? trimString(value.endDate),
    start_time: startTime,
    duration_minutes: durationMinutes,
    when_month: parseWhenMonth(value.when_month) ?? parseWhenMonth(value.whenMonth),
    price: safePrice,
    schedule: mergeBrowseSlotIntoSchedule(schedule, startTime, durationMinutes),
    ...assistantPriceToWire(safePrice),
  };
}

export function parseAssistantTurnDate(value: string | null | undefined): string | null {
  const raw = value?.trim() ?? '';
  if (!raw) {
    return null;
  }
  const day = raw.match(/^(\d{4}-\d{2}-\d{2})/);
  return day ? day[1] : null;
}

export function parseAssistantTurnTime(value: string | null | undefined): string | null {
  const raw = value?.trim() ?? '';
  if (!raw) {
    return null;
  }
  if (/^\d{2}:\d{2}$/.test(raw)) {
    return raw;
  }
  const iso = raw.match(/T(\d{2}:\d{2})/);
  return iso ? iso[1] : null;
}

/**
 * Backend `officeFamily` may be a boolean, “officeFamily”, or an English catalog name.
 * Bare “office / משרד” maps to Private Office only; boolean true keeps the family expand.
 */
export function mapAssistantTurnOfficeFamily(raw: unknown): {
  officeFamily: boolean;
  assetTypeLabel: string | null;
} {
  if (raw === true) {
    return { officeFamily: true, assetTypeLabel: 'Private Office' };
  }
  if (raw === false || raw == null || raw === '') {
    return { officeFamily: false, assetTypeLabel: null };
  }
  const label = String(raw).trim();
  if (!label) {
    return { officeFamily: false, assetTypeLabel: null };
  }
  if (/^officefamily$/i.test(label.replace(/\s+/g, ''))) {
    return { officeFamily: true, assetTypeLabel: 'Private Office' };
  }
  const normalized = label.trim().toLowerCase().replace(/\s+/g, ' ');
  // Bare “office / משרד” is Private Office only (not open space / conference).
  if (isGenericOfficeSearchPhrase(normalized)) {
    return { officeFamily: false, assetTypeLabel: 'Private Office' };
  }
  const spoken = matchBrowseAssetTypeFromSpokenPhrase(label);
  if (spoken) {
    return { officeFamily: false, assetTypeLabel: spoken };
  }
  return { officeFamily: false, assetTypeLabel: label };
}

export function parseAssistantTurnResponse(data: unknown): AssistantTurnResponse | null {
  if (!isRecord(data)) {
    return null;
  }
  const say = typeof data.say === 'string' ? data.say : null;
  const actionRaw = trimString(data.action)?.toLowerCase();
  if (say == null || !actionRaw || !TURN_ACTIONS.has(actionRaw as AssistantTurnAction)) {
    return null;
  }
  return {
    say,
    action: actionRaw as AssistantTurnAction,
    browse: parseBrowse(data.browse),
    assetIds: parseAssetIds(data.asset_ids ?? data.assetIds),
    meta: isRecord(data.meta) ? data.meta : null,
  };
}

export function lastSearchToAssistantTurnWire(
  lastSearch: AssistantSearchIntent | null | undefined,
): AssistantTurnLastSearchWire | undefined {
  if (!lastSearch) {
    return undefined;
  }
  const type = lastSearch.assetTypeLabel?.trim() || null;
  const scheduleWire = assistantScheduleToWire(lastSearch.schedule);
  return {
    city: lastSearch.city?.trim() || null,
    street: lastSearch.streetDisplay?.trim() || lastSearch.street?.trim() || null,
    type,
    officeFamily: lastSearch.officeFamily ? true : type,
    open24h: Boolean(lastSearch.open24h),
    start_date: lastSearch.whenDate?.trim() || null,
    // Range end is until_date when present — not a duplicate of the first occurrence.
    end_date: lastSearch.schedule?.until_date?.trim() || lastSearch.whenDate?.trim() || null,
    ...(scheduleWire ? { schedule: scheduleWire } : {}),
    ...assistantPriceToWire(lastSearch.price),
  };
}

export function assistantSearchIntentFromTurn(
  turn: AssistantTurnResponse,
  utterance: string,
): AssistantSearchIntent | null {
  if (turn.action !== 'search' && turn.action !== 'refine') {
    return null;
  }
  const mapped = mapAssistantTurnOfficeFamily(turn.browse.officeFamily ?? turn.browse.type);
  const city = turn.browse.city;
  const schedule = mergeAssistantScheduleOverlay(
    turn.browse.schedule,
    parseAssistantScheduleFromUtterance(utterance),
  );
  const whenMonth = turn.browse.when_month;
  const whenDate =
    parseAssistantTurnDate(turn.browse.start_date) ??
    (schedule ? nextAssistantScheduleDateKey(schedule) : null) ??
    (whenMonth && /^\d{4}-\d{2}$/.test(whenMonth) ? `${whenMonth}-01` : null);
  const whenTime =
    turn.browse.start_time ??
    schedule?.start_time ??
    parseAssistantTurnTime(turn.browse.start_date) ??
    parseAssistantTurnTime(turn.browse.end_date) ??
    null;
  const amenityIcons = amenityIconsFromTurnFacilities(turn.browse.facilities);
  return {
    kind: 'search',
    keyword: utterance,
    assetTypeLabel: mapped.assetTypeLabel,
    city,
    countryId: city ? ISRAEL_COUNTRY_ID : null,
    street: turn.browse.street,
    streetDisplay: turn.browse.street,
    whenDate,
    whenMonth,
    whenTime,
    open24h: turn.browse.open24h,
    venue: null,
    price: turn.browse.price,
    schedule,
    amenityIcons: amenityIcons.length > 0 ? amenityIcons : null,
    officeFamily: mapped.officeFamily,
    assetIds: turn.assetIds.length > 0 ? turn.assetIds : null,
    raw: utterance,
  };
}

/** True when the turn actually searched the catalog (not a clarify / empty browse). */
export function assistantTurnHasCatalogSearch(turn: AssistantTurnResponse): boolean {
  if (turn.action !== 'search' && turn.action !== 'refine') {
    return false;
  }
  if (turn.assetIds.length > 0) {
    return true;
  }
  const browse = turn.browse;
  if (browse.city?.trim() || browse.street?.trim()) {
    return true;
  }
  if (browse.open24h) {
    return true;
  }
  if (browse.price) {
    return true;
  }
  if (browse.schedule) {
    return true;
  }
  if (browse.facilities && browse.facilities.length > 0) {
    return true;
  }
  if (browse.start_time || browse.duration_minutes != null || browse.when_month) {
    return true;
  }
  if (browse.type?.trim()) {
    return true;
  }
  if (browse.officeFamily === true) {
    return true;
  }
  if (typeof browse.officeFamily === 'string' && browse.officeFamily.trim()) {
    return true;
  }
  return false;
}

/**
 * Use the local HE/EN parser when the turn did not search, or searched without a
 * city that the utterance clearly named (e.g. קרית אונו → backend clarify).
 * Named venues / streets must stay local: turn browse never carries listSearch,
 * and turn asset_ids can hard-gate to empty even when the street has listings
 * (“תמצא לי משרד ברחוב יגאל אלון בתל אביב”).
 * Calendar slots (DD.MM.YYYY, משעה 1200 עד 1800) also stay local — the turn
 * often misreads those and returns asset_ids that wipe the catalog.
 */
export function shouldPreferLocalAssistantSearch(
  turn: AssistantTurnResponse,
  localIntent: {
    kind: string;
    city?: string | null;
    venue?: string | null;
    street?: string | null;
    streetDisplay?: string | null;
    whenDate?: string | null;
    whenMonth?: string | null;
    whenTime?: string | null;
    schedule?: { duration_minutes?: number | null } | null;
    /** Local “עם מטבח” — turn often omits facilities and returns unfiltered asset_ids. */
    amenityIcons?: string[] | null;
  },
): boolean {
  if (localIntent.kind !== 'search') {
    return false;
  }
  if (localIntent.venue?.trim()) {
    return true;
  }
  if (localIntent.street?.trim() || localIntent.streetDisplay?.trim()) {
    return true;
  }
  if (localIntent.amenityIcons && localIntent.amenityIcons.length > 0) {
    return true;
  }
  if (
    localIntent.whenDate?.trim() ||
    localIntent.whenMonth?.trim() ||
    localIntent.whenTime?.trim() ||
    (localIntent.schedule?.duration_minutes != null &&
      localIntent.schedule.duration_minutes > 0)
  ) {
    return true;
  }
  if (!assistantTurnHasCatalogSearch(turn)) {
    return true;
  }
  const localCity = localIntent.city?.trim() ?? '';
  const turnCity = turn.browse.city?.trim() ?? '';
  return Boolean(localCity && !turnCity);
}

export function browseParamsFromAssistantTurn(turn: AssistantTurnResponse): AssistantTurnBrowseParams {
  const mapped = mapAssistantTurnOfficeFamily(turn.browse.officeFamily ?? turn.browse.type);
  const city = turn.browse.city;
  const schedule = turn.browse.schedule;
  const amenityIcons = amenityIconsFromTurnFacilities(turn.browse.facilities);
  const whenMonth = turn.browse.when_month;
  const whenDateFromMonth =
    whenMonth && /^\d{4}-\d{2}$/.test(whenMonth) ? `${whenMonth}-01` : null;
  return {
    catalogBrowse: true,
    returnView: 'home',
    // Ignore leftover turn listSearch (e.g. “אנשים”); amenities use amenityIcons.
    listSearch: null,
    countryId: city ? ISRAEL_COUNTRY_ID : null,
    city,
    street: turn.browse.street,
    assetTypeLabel: mapped.assetTypeLabel,
    officeFamily: mapped.officeFamily,
    open24h: turn.browse.open24h,
    whenDate:
      parseAssistantTurnDate(turn.browse.start_date) ??
      (schedule ? nextAssistantScheduleDateKey(schedule) : null) ??
      whenDateFromMonth,
    whenTime:
      turn.browse.start_time ??
      schedule?.start_time ??
      parseAssistantTurnTime(turn.browse.start_date) ??
      parseAssistantTurnTime(turn.browse.end_date) ??
      null,
    assetIds: turn.assetIds.length > 0 ? turn.assetIds : null,
    price: turn.browse.price,
    schedule,
    amenityIcons: amenityIcons.length > 0 ? amenityIcons : null,
  };
}

/**
 * POST `/assistant/turn` is on unless `REACT_APP_ASSISTANT_TURN` is explicitly false.
 */
export function isAssistantTurnEnabled(): boolean {
  const raw = process.env.REACT_APP_ASSISTANT_TURN?.trim().toLowerCase();
  if (raw === 'false' || raw === '0' || raw === 'off' || raw === 'no') {
    return false;
  }
  return true;
}

export async function postAssistantTurn(options: {
  utterance: string;
  language?: AppLanguage;
  currentScreen?: AppView | string | null;
  lastSearch?: AssistantSearchIntent | null;
}): Promise<AssistantTurnResponse | null> {
  if (!isAssistantTurnEnabled()) {
    return null;
  }
  const utterance = options.utterance.trim();
  if (!utterance) {
    return null;
  }
  try {
    const lastSearch = lastSearchToAssistantTurnWire(options.lastSearch);
    const data = await requestAssetApi({
      method: 'POST',
      path: ASSISTANT_TURN_PATH,
      body: {
        utterance,
        language: options.language === 'en' ? 'en' : 'he',
        current_screen: options.currentScreen ? String(options.currentScreen).trim() || 'home' : 'home',
        ...(lastSearch ? { last_search: lastSearch } : {}),
      },
    });
    return parseAssistantTurnResponse(data);
  } catch {
    return null;
  }
}
